import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiryTime: string | null;
  status: 'checking' | 'authenticated' | 'anonymous';
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface LoginSuccessPayload {
  accessToken: string;
  refreshToken: string | null;
  tokenExpiryTime: string;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  tokenExpiryTime: null,
  status: 'checking',
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiryTime = action.payload.tokenExpiryTime;
      state.status = 'authenticated';
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.status = 'anonymous';
    },
    sessionRestored: (state, action: PayloadAction<LoginSuccessPayload>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiryTime = action.payload.tokenExpiryTime;
      state.status = 'authenticated';
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.tokenExpiryTime = null;
      state.status = 'anonymous';
      state.isAuthenticated = false;
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  sessionRestored,
  logout,
  updateUser,
} = authSlice.actions;

export default authSlice.reducer;
